import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { LogoRed, MenuIcon, CloseIcon } from './svg';
import { BodyTextL } from './text';
import { BlackNavigationLink, NavigationLink } from '../components/link';
import { RED } from '../utils/color-constants';
import { PAGES_INFO, MEDIA_INFO } from '../utils/pages-info';
import createTranslate from '../utils/translator-provider';

const mainLinkStyle = 'h-20 pl-3 md:pl-6 text-black font-extra-bold leading-loose hover:text-red';
const subLinksContainerStyle = `flex flex-col items-start pl-3 md:px-6 pb-6 md:absolute bg-white md:hidden md:group-hover:flex md:group-hover:flex-col md:shadow-md`;
const subLinkStyle = 'mt-2 text-black font-core';

const Navigation = () => {
  const data = useStaticQuery(graphql`
    query {
      translations: allContentfulTranslation {
        nodes {
          key
          value {
            value
          }
        }
      }

      campaigns: allContentfulCampaign {
        nodes {
          id
          showInNavigationBar
          nameInNavigationBar
          slug
        }
      }
    }
  `);

  const translate = createTranslate(data.translations.nodes);

  const buttonShowStyle = 'w-10 md:hidden';
  const buttonHideStyle = 'hidden';

  const getLinkContainerStyle = displayClassname => {
    return `w-full xl:w-7/12 sm:mb-4 lg:mb-0 md:flex flex-row justify-between
      items-center relative bg-white ${displayClassname}`;
  };

  const [linkContainerStyle, setLinkContainerStyle] = useState(
    getLinkContainerStyle('hidden')
  );
  const [menuButtonStyle, setMenuButtonStyle] = useState(buttonShowStyle);
  const [closeButtonStyle, setCloseButtonStyle] = useState(buttonHideStyle);

  const handleMenuButtonClick = () => {
    setMenuButtonStyle(buttonHideStyle);
    setCloseButtonStyle(buttonShowStyle);
    setLinkContainerStyle(getLinkContainerStyle('block'));
  };

  const handleCloseButtonClick = () => {
    setMenuButtonStyle(buttonShowStyle);
    setCloseButtonStyle(buttonHideStyle);
    setLinkContainerStyle(getLinkContainerStyle('hidden'));
  };

  return (
    <nav className="w-full z-40 flex flex-row justify-center bg-white shadow-md relative md:sticky top-0">
      <div className="w-11/12 lg:max-w-1200px block lg:flex flex-row justify-between">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <NavigationLink
              to={PAGES_INFO.landingPage.path}
              className="flex items-center"
            >
              <div className="w-20">
                <LogoRed />
              </div>
              <BodyTextL color={RED}>{translate('nav.title.main')}</BodyTextL>
            </NavigationLink>
          </div>
          <button
            onClick={() => handleMenuButtonClick()}
            className={menuButtonStyle}
          >
            <MenuIcon />
          </button>
          <button
            onClick={() => handleCloseButtonClick()}
            className={closeButtonStyle}
          >
            <CloseIcon />
          </button>
        </div>

        <div className={linkContainerStyle}>
          <ForAdvertiserLinks translate={translate} />
          <SolutionsLinks translate={translate} />

          <CurrentLinks
            translate={translate}
            campaigns={data.campaigns.nodes}
          />
          <MediaInformationLinks translate={translate} />

          <div className="pb-6 md:pb-0">
            <BlackNavigationLink
              to={PAGES_INFO.contact.path}
              className={mainLinkStyle}
            >
              {translate('nav.link.contacts')}
            </BlackNavigationLink>
          </div>
        </div>
      </div>
    </nav>
  );
};

const ForAdvertiserLinks = ({ translate }) => {
  return (
    <div className="group">
      <BlackNavigationLink
        to={PAGES_INFO.advertiserLanding.path}
        className={mainLinkStyle}
      >
        {translate('nav.link.forAdvertiser')}
      </BlackNavigationLink>
      <div className={subLinksContainerStyle}>
        <BlackNavigationLink to={PAGES_INFO.advertiserLanding.path} className={subLinkStyle}>
          {translate('nav.link.adpackages')}
        </BlackNavigationLink>
        <BlackNavigationLink to={PAGES_INFO.uusimaa.path} className={subLinkStyle}>
          {translate('nav.link.uusimaa')}
        </BlackNavigationLink>
        <BlackNavigationLink to={PAGES_INFO.planning.path} className={subLinkStyle}>
          {translate('nav.link.planning')}
        </BlackNavigationLink>
        <BlackNavigationLink
          to={PAGES_INFO.newAdvertiser.path}
          className={subLinkStyle}
        >
          {translate('nav.link.forNewAdvertiser')}
        </BlackNavigationLink>
        <BlackNavigationLink
          to={PAGES_INFO.localAdvertising.path}
          className={subLinkStyle}
        >
          {translate('nav.link.localAdvertising')}
        </BlackNavigationLink>
        <BlackNavigationLink to={PAGES_INFO.channels.path} className={subLinkStyle}>
          {translate('nav.link.channels')}
        </BlackNavigationLink>
        <BlackNavigationLink to={PAGES_INFO.references.path} className={subLinkStyle}>
          {translate('nav.link.references')}
        </BlackNavigationLink>
      </div>
    </div>
  );
};

const SolutionsLinks = ({ translate }) => {
  return (
    <div className="group pb-6 md:pb-0">
      <BlackNavigationLink
        to={PAGES_INFO.showRoom.path}
        className={mainLinkStyle}
      >
        {translate('nav.link.solutions')}
      </BlackNavigationLink>
    </div>
  )
}

const CurrentLinks = ({ translate, campaigns }) => {
  return (
    <div className="group">
      <BlackNavigationLink
        to={PAGES_INFO.articles.path}
        className={mainLinkStyle}
      >
        {translate('nav.link.current')}
      </BlackNavigationLink>

      {campaigns.length > 0 ? (
        <div className={subLinksContainerStyle}>
          <BlackNavigationLink
            to={PAGES_INFO.articles.path}
            className={subLinkStyle}
          >
            {translate('nav.link.articles')}
          </BlackNavigationLink>
          <BlackNavigationLink
            to={PAGES_INFO.campaigns.path}
            className={subLinkStyle}
          >
            {translate('nav.link.campaigns')}
          </BlackNavigationLink>
          {campaigns
            .filter(campaign => campaign.showInNavigationBar === true)
            .map((campaign, index) => {
              return (
                <BlackNavigationLink
                  key={index}
                  to={PAGES_INFO.campaigns.path + `/${campaign.slug}`}
                  className={subLinkStyle}
                >
                  {campaign.nameInNavigationBar}
                </BlackNavigationLink>
              );
            })
          }
        </div>
      ) : null}
    </div>
  );
};

const MediaInformationLinks = ({ translate }) => {
  return (
    <div className="group">
      <BlackNavigationLink
        to={PAGES_INFO.mediaInfo.path}
        className={mainLinkStyle}
      >
        {translate('nav.link.mediaInfo')}
      </BlackNavigationLink>
      <div className={subLinksContainerStyle}>
        <BlackNavigationLink
          to={MEDIA_INFO.totalTv.path}
          className={subLinkStyle}
        >
          {translate('nav.link.totalTVPrices')}
        </BlackNavigationLink>
        <BlackNavigationLink
          to={MEDIA_INFO.onlineAdvertising.path}
          className={subLinkStyle}
        >
          {translate('nav.link.onlineAdvertisingPrices')}
        </BlackNavigationLink>
        <BlackNavigationLink
          to={MEDIA_INFO.material.path}
          className={subLinkStyle}
        >
          {translate('nav.link.material')}
        </BlackNavigationLink>
        <BlackNavigationLink
          to={MEDIA_INFO.salesConditions.path}
          className={subLinkStyle}
        >
          {translate('nav.link.salesConditions')}
        </BlackNavigationLink>
      </div>
    </div>
  )
}

export default Navigation;
